<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { buildGrid, squareKey, GRID_SIZE, type GridCell } from '@/api/grid';
import { t } from '@/i18n';
import type { Coordinate, Square } from '@/api/types';

const props = defineProps<{
  squares: Square[];
  columnDigits: number[];
  rowDigits: number[];
  /**
   * The teams the two axes stand for. Optional, because the landing page's example grid has no
   * game behind it — everywhere else they are the answer to the question people kept asking,
   * which is whose score picks the column.
   */
  teamA?: string;
  teamB?: string;
  /**
   * What to paint the two bars. One of the six in the contract's `TeamColour`; anything else —
   * including a game stored before the field existed — draws gold, which is the API's default
   * and the colour the artwork uses for the top bar.
   */
  teamAColour?: string;
  teamBColour?: string;
  /** Coordinates the current user has selected but not yet submitted. */
  selected?: Coordinate[];
  /** Revealed once a final score is in. */
  winning?: Coordinate | null;
  /**
   * Let the board shrink to its container instead of holding the 420px floor and scrolling.
   *
   * Only for a board that is watched rather than played: the floor exists so a cell is big enough
   * to hit with a thumb and a name is big enough to read before you claim next to it, and neither
   * applies to the landing page's example. What does apply there is that a quarter of the board
   * sitting off the side of a phone is a quarter of the story nobody sees.
   */
  compact?: boolean;
  /**
   * Where the predicted final score lands, if one was worked out. Drawn as a frame around the
   * cell rather than a fill: the fill already says won, yours-or-selected, somebody's or free,
   * and a guess is none of those things.
   */
  predicted?: Coordinate | null;
  /**
   * The prediction has been asked for and has not come back. Words, not a frame — there is no
   * square to point at yet. Ignored once `predicted` arrives, which is the same poll.
   */
  predicting?: boolean;
  interactive?: boolean;
  /**
   * Whether a square somebody already holds is tappable too. Picking says no: that square is
   * somebody else's, so a tap on it could only be a mis-tap. The host's override says yes —
   * tapping the square that *got* tapped by mistake is the entire interaction.
   */
  canTapHeld?: boolean;
  /**
   * Whose squares to draw as *yours*, and — while interactive — let them tap to give one back.
   *
   * Only the player's own grid passes this. On a board where a tap claims outright, "which of
   * these hundred are mine" is the state the whole screen turns on, and a name in a cell the
   * same colour as every other name does not answer it from arm's length.
   */
  ownerId?: string | null;
}>();

const emit = defineEmits<{ toggle: [Coordinate] }>();

/*
 * The six the host may choose from, and the class that paints each. Listed rather than
 * interpolated into `team-${colour}`, because a colour that arrived from an API response would
 * then be able to name any class in the stylesheet.
 */
const TEAM_CLASSES: Record<string, string> = {
  gold: 'team-gold',
  black: 'team-black',
  sky: 'team-sky',
  red: 'team-red',
  blue: 'team-blue',
  green: 'team-green',
};

const teamClass = (colour: string | undefined): string => TEAM_CLASSES[colour ?? ''] ?? 'team-gold';

/**
 * Rows of cells rather than one flat list, so the template can interleave the row label
 * without relying on the two staying in step.
 */
const rows = computed(() => {
  const cells = buildGrid(props.squares);
  return Array.from({ length: GRID_SIZE }, (_, row) =>
    cells.slice(row * GRID_SIZE, row * GRID_SIZE + GRID_SIZE),
  );
});

const selectedKeys = computed(
  () => new Set((props.selected ?? []).map((c) => squareKey(c.col, c.row))),
);

const isSelected = (cell: GridCell) => selectedKeys.value.has(squareKey(cell.col, cell.row));
const isWinner = (cell: GridCell) =>
  props.winning?.col === cell.col && props.winning?.row === cell.row;
/** Held, by the person reading. Never true without an `ownerId`, so other grids are unaffected. */
const isMine = (cell: GridCell) =>
  Boolean(props.ownerId) && cell.square?.subjectId === props.ownerId;

/**
 * On the winning square's column or row — but never on the square itself, which is already the
 * loudest thing on the board and needs no underlining.
 *
 * The crosshair is the campaign artwork's, and it is there for the same reason: it leads the eye
 * to the crossing instead of leaving somebody to find one gold tile among a hundred. It follows
 * `winning` rather than being asked for, because a screen that reveals a winner and does not
 * explain which two digits picked it is every screen that reveals a winner.
 */
const isCrosshair = (cell: GridCell): boolean =>
  Boolean(props.winning) &&
  !isWinner(cell) &&
  (props.winning?.col === cell.col || props.winning?.row === cell.row);

/** A digit the final score landed on. Indexes, not values: the digits are a lookup by position. */
const isLitDigit = (index: number, axisIsColumns: boolean): boolean =>
  axisIsColumns ? props.winning?.col === index : props.winning?.row === index;

/**
 * Never true on the winning square. A correct prediction is exactly where keeping the frame is
 * most tempting and most wrong: the reveal is the moment the whole app builds to, and the square
 * that won has to look like nothing else on the board.
 */
/*
 * What the reader can actually see of the board, and it drives two things.
 *
 * **The fade at each edge.** It is what says there is more board off the side of the phone — 46%
 * of it, at rest, on a 390px screen. It has to fade *whichever* side has something behind it, and
 * stop at whichever side does not: the 24px it dims is half a column of names, and reading a name
 * is the whole reason this board is 600px wide.
 *
 * A listener rather than CSS, because no stylesheet can compare a scroll offset to a content
 * width. Passive, one per board, and it writes three values.
 *
 * **Team A's name is not in here, and was.** Reporting how much of the bar was off screen let CSS
 * re-centre the text in what was left, which was correct and felt wrong: a handler that changes
 * layout runs on the main thread, a scroll does not, and behind a fling the name lagged and
 * jumped. It is `position: sticky` now — the same intention, handed to the compositor.
 */
const scroller = ref<HTMLElement | null>(null);
const overflowing = ref(false);
const fade = reactive({ start: 0, end: 0 });

/** How far the fade reaches in, when there is anything to fade. Matches `--team-bar`. */
const FADE_PX = 24;

const measure = (): void => {
  const el = scroller.value;
  if (!el) return;

  const hiddenLeft = el.scrollLeft;
  const hiddenRight = el.scrollWidth - el.clientWidth - el.scrollLeft;

  // A pixel of slop, because scrollLeft is fractional on a zoomed or high-DPI screen and half a
  // pixel of overflow is not "there is more board".
  overflowing.value = el.scrollWidth - el.clientWidth > 1;
  fade.start = hiddenLeft > 1 ? FADE_PX : 0;
  fade.end = hiddenRight > 1 ? FADE_PX : 0;
};

let observer: ResizeObserver | undefined;

onMounted(() => {
  measure();
  /*
   * The board's own width changes with the container, not just with the window — a panel opening
   * beside it moves the scroll geometry without a resize event.
   *
   * Guarded, because jsdom has no ResizeObserver and every board in every component test mounts
   * through here. Nothing is lost there: this is layout, and jsdom has none — `measure` above
   * already leaves a board that does not overflow with no fade and a centred name.
   */
  if (typeof ResizeObserver === 'undefined' || !scroller.value) return;
  observer = new ResizeObserver(measure);
  observer.observe(scroller.value);
});

onBeforeUnmount(() => observer?.disconnect());

/*
 * One tab stop for a hundred squares, and the arrow keys the `role="grid"` already promised.
 *
 * The role tells a screen-reader user to expect cell navigation, and AT grid modes can trap
 * somebody who tries it — there was none. Meanwhile every cell was a tab stop, so reaching the
 * share panel *below* the board cost a hundred Tab presses, and on a read-only board the cells
 * were `div`s that could not be focused at all.
 *
 * The roving index is a flat cell number, `row * 10 + col`, which is the order the grid renders
 * in. It follows focus as well as driving it, so a pointer user who taps a square and then
 * reaches for the keyboard carries on from there rather than from the top-left.
 */
const active = ref(0);
const gridEl = ref<HTMLElement | null>(null);

const cellIndex = (cell: GridCell): number => cell.row * GRID_SIZE + cell.col;

/** Stops at the edges: wrapping would make a board read as one long list of a hundred. */
const clampToGrid = (col: number, row: number): number =>
  Math.min(Math.max(row, 0), GRID_SIZE - 1) * GRID_SIZE + Math.min(Math.max(col, 0), GRID_SIZE - 1);

async function onGridKey(event: KeyboardEvent): Promise<void> {
  const col = active.value % GRID_SIZE;
  const row = Math.floor(active.value / GRID_SIZE);

  const moves: Record<string, number | undefined> = {
    ArrowRight: clampToGrid(col + 1, row),
    ArrowLeft: clampToGrid(col - 1, row),
    ArrowDown: clampToGrid(col, row + 1),
    ArrowUp: clampToGrid(col, row - 1),
    // The ends of the *row*, which is what Home and End mean in a grid.
    Home: clampToGrid(0, row),
    End: clampToGrid(GRID_SIZE - 1, row),
    // ...and the ends of the column, which is the nearest sensible reading of a page on a board
    // ten rows deep: a page *is* the board.
    PageUp: clampToGrid(col, 0),
    PageDown: clampToGrid(col, GRID_SIZE - 1),
  };

  const next = moves[event.key];
  if (next === undefined) return;

  // Only for a key that moved something: the grid must not eat Tab, Enter or a space.
  event.preventDefault();
  active.value = next;

  await nextTick();
  gridEl.value?.querySelector<HTMLElement>(`[data-cell="${next}"]`)?.focus();
}

const isPredicted = (cell: GridCell) =>
  !isWinner(cell) && props.predicted?.col === cell.col && props.predicted?.row === cell.row;

function classesFor(cell: GridCell): string {
  // `square-won` is the overshoot: at cell size a colour change alone is one tile in a hundred,
  // which is why the ad draws the won tile 30% over size too.
  //
  // Each fill names its own text colour rather than inheriting the page's. It reads as belt and
  // braces in the light theme, where `on-win` and `on-fill` are both the same near-black `ink`
  // falls back to — but gold does not move between themes and the page's ink does, so a dark
  // theme would put near-white type on the winning square if this were left to inheritance.
  if (isWinner(cell)) return 'bg-win text-on-win font-bold ring-2 ring-win-ring square-won';
  // Selected and yours share a fill — on the one screen that has both, a selection *becomes* a
  // square of yours.
  if (isSelected(cell) || isMine(cell)) return 'bg-accent text-on-accent';
  if (cell.square) return 'bg-fill text-on-fill';
  return 'bg-square-free hover:bg-surface-sunken';
}

/** Interactive, and this particular cell may be tapped. */
const tappable = (cell: GridCell): boolean =>
  Boolean(props.interactive) && (Boolean(props.canTapHeld) || !cell.square || isMine(cell));

function describe(cell: GridCell): string {
  const where = { col: cell.col, row: cell.row };
  const named = cell.square ? { ...where, name: cell.square.displayName } : where;
  const base = !cell.square
    ? t('grid.cellFree', where)
    : isMine(cell)
      ? t('grid.cellYours', named)
      : t('grid.cellTaken', named);
  // Three visual signals say this square won — the gold, the ring and the trophy — and none of
  // them is any use to somebody reading the board rather than looking at it.
  if (isWinner(cell)) return `${base}${t('grid.cellWon')}`;
  // A frame is no use to somebody who cannot see it, and the caption below the board says
  // "the framed square" — which names nothing at all without this.
  return isPredicted(cell) ? `${base}${t('grid.cellPredicted')}` : base;
}
</script>

<template>
  <!--
    The scroller, and now a wrapper around it: the caption below is prose, and prose does not
    belong inside a 600px frame on a 324px screen. See the note above it.

    `board` carries the shared measurements — `--team-bar` and the two gutters — because they are
    read by the bars, the grid *and* the caption, and this is the only element all four are inside.
  -->
  <div class="board">
    <div
      ref="scroller"
      class="overflow-x-auto"
      :class="overflowing && 'board-scroller'"
      :style="{ '--board-fade-start': `${fade.start}px`, '--board-fade-end': `${fade.end}px` }"
      @scroll.passive="measure"
    >
      <!-- Fixed min-width so the grid stays square and legible on a phone rather than squashing
           into unreadable slivers; the container scrolls instead. It covers the side label too, so
           naming the teams did not make the board any wider than it already was. `compact` is the
           one board that would rather be whole than be legible — see the prop.

           **600px, and it used to be 420.** On a 390px phone that floor left a cell 34px across
           carrying 10px type, and a name got three letters and an ellipsis — so the board could say
           a square was taken but not by whom, which is the question somebody has while they are
           choosing where to put their own name. 600 buys a 52px cell and room for about seven
           characters, at the price of a board a phone has to be swiped sideways to see all of.
           That is the trade, made deliberately: the names are most of why people play this with
           people they know. Nothing changes above 600px — every column is `1fr`, so a desktop board
           is the width it always was. test/e2e/board-legibility.spec.ts measures both halves. -->
      <div class="board-frame" :class="compact ? 'board-frame-compact min-w-0' : 'min-w-[600px]'">
        <!-- The board, and only the board. Team B's bar has no height of its own — it is upended
             text stretched by this row — so anything that sits *below* the board must sit outside
             the row, or the bar runs down the side of it. See `.team-axis-b` in main.css. -->
        <div class="flex items-stretch gap-1">
          <!-- Down the side, turned to read bottom-to-top: the rows are this team's last digit. -->
          <p
            v-if="teamB"
            class="team-axis team-axis-b font-semibold"
            :class="[teamClass(teamBColour), teamA && 'team-axis-b-under-a']"
          >
            {{ teamB }}
          </p>

          <div class="min-w-0 grow">
            <!-- Along the top, over the hundred cells rather than over the row labels too. -->
            <!--
              The name is in a span so the bar can be left-aligned without the *bar* moving: the
              box still spans the board, and the text starts where the first column does. Centred,
              it sat at ~329px of a 600px frame with 324px of it visible on a 390px phone, and
              "Springboks" rendered as "Sprin" — the label that answers whose score picks the
              column, missing on the screen that needs it most.
            -->
            <p
              v-if="teamA"
              class="team-axis team-axis-a font-semibold"
              :class="teamClass(teamAColour)"
            >
              <span>{{ teamA }}</span>
            </p>

            <!--
              A real ARIA grid, not a hundred divs.

              The cells carried an `aria-label` and, when not tappable, rendered as a bare `div` —
              and `aria-label` on an implicit `role="generic"` is a prohibited attribute that
              browsers do not expose. So on every read-only board — a locked game, a *completed*
              game, the host's view — no square had an accessible name at all, and the caption below
              still said "the framed square".

              The row wrappers use `display: contents` so the cells stay direct children of the CSS
              grid: the layout is unchanged and the structure is the part that is new.

              **`gridcell` only where the cell is not tappable.** An explicit role replaces the
              implicit one, so putting it on the `<button>` too took the button role away — a
              screen reader announced "gridcell" for something you can press, and Playwright's
              `getByRole('button')` stopped finding a hundred cells. A tappable cell keeps its
              native button role; the read-only ones, which had no exposed name at all, get
              `gridcell`. Strict ARIA would want a `gridcell` wrapping each button, which means a
              second element inside the CSS grid — not worth the layout risk on this screen for a
              structure both browsers and assistive technology already read correctly.
            -->
            <div
              ref="gridEl"
              class="squares-grid"
              role="grid"
              :aria-label="t('grid.board')"
              @keydown="onGridKey"
            >
              <div class="grid-row" role="row">
                <!-- Corner: the axes meet here and it holds nothing. -->
                <div class="axis-label" role="presentation" aria-hidden="true" />

                <!-- Team A runs along the columns — the last digit of their score picks the column. -->
                <div
                  v-for="(digit, col) in columnDigits"
                  :key="`col-${col}`"
                  role="columnheader"
                  class="axis-label font-semibold"
                  :class="
                    isLitDigit(col, true) ? 'axis-digit-lit bg-win text-on-win' : 'text-ink-muted'
                  "
                >
                  {{ digit }}
                </div>
              </div>

              <div v-for="(cells, row) in rows" :key="`row-${row}`" class="grid-row" role="row">
                <!-- Team B runs down the rows. -->
                <div
                  role="rowheader"
                  class="axis-label font-semibold"
                  :class="
                    isLitDigit(row, false) ? 'axis-digit-lit bg-win text-on-win' : 'text-ink-muted'
                  "
                >
                  {{ rowDigits[row] ?? row }}
                </div>

                <component
                  :is="tappable(cell) ? 'button' : 'div'"
                  v-for="cell in cells"
                  :key="squareKey(cell.col, cell.row)"
                  :type="tappable(cell) ? 'button' : undefined"
                  :role="tappable(cell) ? undefined : 'gridcell'"
                  class="square-cell rounded-sm border border-line"
                  :class="[
                    classesFor(cell),
                    isPredicted(cell) && 'square-predicted guess-frame',
                    isCrosshair(cell) && 'square-crosshair',
                  ]"
                  :aria-label="describe(cell)"
                  :aria-pressed="tappable(cell) ? isSelected(cell) || isMine(cell) : undefined"
                  :data-cell="cellIndex(cell)"
                  :tabindex="cellIndex(cell) === active ? 0 : -1"
                  @focus="active = cellIndex(cell)"
                  @click="tappable(cell) && emit('toggle', { col: cell.col, row: cell.row })"
                >
                  <!-- Empty cells stay blank: the axis labels already say which score they are, and
                     printing it in all 100 cells buries the names that matter. Screen readers get
                     the coordinate from aria-label. -->
                  <!-- Beside the name, which is whose square it is. Decoration on top of the fill
                     and the ring; the aria-label is where the same thing is said in words. -->
                  <span v-if="isWinner(cell)" aria-hidden="true" class="square-trophy">🏆</span>
                  <span v-if="cell.square" class="truncate">{{ cell.square.displayName }}</span>
                </component>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--
      The frame is a mark; this is what makes it mean something. Kept next to the board rather
      than on the page, so the two cannot be separated by a later refactor.

      Outside the scroller, not just outside the flex row.

      It measured 572px inside a 324px viewport on a phone — wrapping to the *board's* width and
      running a quarter of a screen past the visible edge on every line, silently cut rather than
      ellipsised. Prose that needs horizontal scrolling is what SC 1.4.10 Reflow is about, and the
      caption explains the one thing on the board a reader cannot work out for themselves.

      The inset class stays: it still lines up with the board's left edge rather than the card's.
    -->
    <p
      v-if="predicted"
      class="grid-caption mt-2 text-xs text-ink-muted"
      :class="teamB && 'grid-caption-inset'"
    >
      {{ t('grid.predictedCaption') }}
    </p>
    <!-- Nothing when no prediction was asked for: the kill switch being off and a fixture
         nobody could resolve are both non-events, not errors to explain. -->
    <p
      v-else-if="predicting"
      class="grid-caption mt-2 text-xs text-ink-muted"
      :class="teamB && 'grid-caption-inset'"
    >
      {{ t('grid.predictingCaption') }}
    </p>
  </div>
</template>
